import { FormEvent, ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, supabaseConfigured } from './lib/supabase';

export type MarineOrganizationType =
  | 'platform_internal'
  | 'brokerage'
  | 'marine_repair'
  | 'marina_yard'
  | 'service_vendor'
  | 'dealer_sales'
  | 'vessel_owner';

export interface MarineOrganizationAccess {
  id: string;
  name: string;
  slug: string;
  organizationType: MarineOrganizationType;
  status: string;
  roles: string[];
  modules: string[];
}

export interface MarineAccess {
  userId: string;
  email: string;
  isPlatformAdmin: boolean;
  organizations: MarineOrganizationAccess[];
}

type AccessState =
  | { status: 'checking' }
  | { status: 'approved'; value: MarineAccess }
  | { status: 'onboarding' };

const MarineAccessContext = createContext<MarineAccess | null>(null);

export function useMarineAccess() {
  const value = useContext(MarineAccessContext);
  if (!value) throw new Error('Marine access context is unavailable.');
  return value;
}

export default function AuthGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(supabaseConfigured);
  const [access, setAccess] = useState<AccessState>({ status: 'checking' });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!supabase) return;

    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
      setAccess({ status: 'checking' });
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!supabase || !session?.user) return;

    let cancelled = false;
    async function checkAccess() {
      setAccess({ status: 'checking' });

      const [adminResult, membershipResult] = await Promise.all([
        supabase!.from('marine_platform_admins').select('user_id').eq('user_id', session!.user.id).maybeSingle(),
        supabase!.from('marine_organization_members').select('organization_id, role').eq('user_id', session!.user.id).eq('active', true),
      ]);

      if (cancelled) return;
      if (adminResult.error) console.error('MarineOps platform-admin check failed', adminResult.error);
      if (membershipResult.error) {
        console.error('MarineOps membership check failed', membershipResult.error);
        setAccess({ status: 'onboarding' });
        return;
      }

      const memberships = membershipResult.data || [];
      const isPlatformAdmin = Boolean(adminResult.data);
      if (!isPlatformAdmin && memberships.length === 0) {
        setAccess({ status: 'onboarding' });
        return;
      }

      const organizationIds = [...new Set(memberships.map((row) => String(row.organization_id)))];
      const [organizationsResult, modulesResult] = organizationIds.length
        ? await Promise.all([
            supabase!
              .from('marine_organizations')
              .select('id, name, slug, organization_type, status')
              .in('id', organizationIds),
            supabase!
              .from('marine_organization_modules')
              .select('organization_id, module_key, enabled')
              .in('organization_id', organizationIds)
              .eq('enabled', true),
          ])
        : [{ data: [], error: null }, { data: [], error: null }];

      if (cancelled) return;
      if (organizationsResult.error) console.error('MarineOps organization lookup failed', organizationsResult.error);
      if (modulesResult.error) console.error('MarineOps module lookup failed', modulesResult.error);

      const organizations: MarineOrganizationAccess[] = (organizationsResult.data || []).map((org) => {
        const orgId = String(org.id);
        return {
          id: orgId,
          name: String(org.name),
          slug: String(org.slug),
          organizationType: String(org.organization_type) as MarineOrganizationType,
          status: String(org.status),
          roles: memberships.filter((member) => String(member.organization_id) === orgId).map((member) => String(member.role)),
          modules: (modulesResult.data || []).filter((module) => String(module.organization_id) === orgId).map((module) => String(module.module_key)),
        };
      });

      setAccess({
        status: 'approved',
        value: {
          userId: session!.user.id,
          email: session!.user.email || '',
          isPlatformAdmin,
          organizations,
        },
      });
    }

    checkAccess();
    return () => { cancelled = true; };
  }, [session, refreshKey]);

  if (!supabaseConfigured) {
    return (
      <>
        <div className="environment-banner">Preview mode · Supabase environment variables are not configured.</div>
        {children}
      </>
    );
  }

  if (loading) return <FullScreenMessage title="Connecting to MarineOps" text="Opening your secure marine workspace…" />;
  if (!session) return <AuthScreen />;
  if (access.status === 'checking') return <FullScreenMessage title="Loading your workspace" text="Checking your organizations, modules and permissions…" />;
  if (access.status === 'onboarding') {
    return <WorkspaceOnboarding email={session.user.email || ''} onCreated={() => setRefreshKey((current) => current + 1)} />;
  }

  return (
    <MarineAccessContext.Provider value={access.value}>
      <div className="environment-banner live">MarineOps · Secure workspace</div>
      {children}
    </MarineAccessContext.Provider>
  );
}

function AuthScreen() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!supabase) return;

    setBusy(true);
    setMessage('');
    setError('');
    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') || '').trim();
    const password = String(form.get('password') || '');

    try {
      if (mode === 'signin') {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
      } else {
        const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
        if (signUpError) throw signUpError;
        if (!data.session) setMessage('Account created. Check your email to confirm the address, then sign in.');
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to authenticate.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-shade" />
      <div className="auth-layout">
        <section className="auth-intro">
          <div className="auth-intro-brand"><span>A</span><div><strong>MarineOps</strong><small>by Aqua Operations</small></div></div>
          <p className="auth-kicker">One connected marine operating platform</p>
          <h2>Brokerage. Repair. Ownership. Service.</h2>
          <p>Run the business side of boating from one secure workspace, then connect directly with other MarineOps businesses when a deal, repair, inspection, marina or owner workflow crosses company lines.</p>
          <div className="auth-module-row"><span>Brokerage</span><span>Marine Repair</span><span>Owner Portal</span><span>Service Network</span></div>
        </section>

        <section className="auth-card">
          <div className="auth-brand"><span>A</span><div><strong>MarineOps</strong><small>aqua-operations.com</small></div></div>
          <p className="eyebrow">Secure Marine Workspace</p>
          <h1>{mode === 'signin' ? 'Sign in to MarineOps' : 'Create your MarineOps account'}</h1>
          <p className="auth-copy">Your account loads only the organizations, roles and modules assigned to you.</p>

          <form onSubmit={submit} className="auth-form">
            <label>Email<input name="email" type="email" autoComplete="email" required placeholder="name@example.com" /></label>
            <label>Password<input name="password" type="password" minLength={8} autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} required /></label>
            {error && <div className="auth-alert error">{error}</div>}
            {message && <div className="auth-alert success">{message}</div>}
            <button className="primary-button auth-submit" disabled={busy}>{busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}</button>
          </form>

          <button className="auth-mode" onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); setMessage(''); }}>
            {mode === 'signin' ? 'New to MarineOps? Create an account' : 'Already have an account? Sign in'}
          </button>
          <small className="auth-footnote">New businesses can create a brokerage, marine-repair, marina, vendor, dealer or owner workspace after account verification.</small>
        </section>
      </div>
    </div>
  );
}

function WorkspaceOnboarding({ email, onCreated }: { email: string; onCreated: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [orgType, setOrgType] = useState<Exclude<MarineOrganizationType, 'platform_internal'>>('marine_repair');
  const [name, setName] = useState('');
  const slug = useMemo(() => name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''), [name]);

  async function createWorkspace(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!supabase) return;
    setBusy(true);
    setError('');
    const { error: createError } = await supabase.functions.invoke('marine-create-workspace', {
      body: {
        org_name: name,
        org_slug: slug,
        org_type: orgType,
      },
    });
    setBusy(false);
    if (createError) {
      setError(createError.message);
      return;
    }
    onCreated();
  }

  return (
    <div className="auth-shell onboarding-shell">
      <div className="auth-shade" />
      <section className="auth-card onboarding-card">
        <div className="auth-brand"><span>A</span><div><strong>MarineOps</strong><small>{email}</small></div></div>
        <p className="eyebrow">Workspace Setup</p>
        <h1>Create your MarineOps workspace</h1>
        <p className="auth-copy">Choose the operation that best matches your business. MarineOps will enable the right starting modules automatically.</p>
        <form onSubmit={createWorkspace} className="auth-form">
          <label>Business / workspace name<input value={name} onChange={(event) => setName(event.target.value)} required placeholder="e.g. Pacific Marine Service" /></label>
          <label>Workspace type
            <select value={orgType} onChange={(event) => setOrgType(event.target.value as Exclude<MarineOrganizationType, 'platform_internal'>)}>
              <option value="marine_repair">Marine Repair</option>
              <option value="brokerage">Brokerage</option>
              <option value="marina_yard">Marina / Yard</option>
              <option value="service_vendor">Service Vendor / Specialist</option>
              <option value="dealer_sales">Dealer / Sales</option>
              <option value="vessel_owner">Vessel Owner</option>
            </select>
          </label>
          <label>Workspace URL<input value={slug} readOnly /></label>
          {error && <div className="auth-alert error">{error}</div>}
          <button className="primary-button auth-submit" disabled={busy || !slug}>{busy ? 'Creating workspace…' : 'Create workspace'}</button>
        </form>
        <button className="auth-mode" onClick={() => supabase?.auth.signOut()}>Sign out</button>
      </section>
    </div>
  );
}

function FullScreenMessage({ title, text, action }: { title: string; text: string; action?: ReactNode }) {
  return (
    <div className="auth-shell">
      <div className="auth-shade" />
      <section className="auth-card compact-card">
        <div className="auth-brand"><span>A</span><div><strong>MarineOps</strong><small>aqua-operations.com</small></div></div>
        <h1>{title}</h1>
        <p className="auth-copy">{text}</p>
        {action}
      </section>
    </div>
  );
}
